import { useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getCachedIdToken } from '../../services/network';
import {
  PASS_DAYS,
  formatPassEnd,
  isDefinitivePassResult,
  passReasonMessage,
  readStashedPassCode,
  redeemPassCode,
  subscribeStashedPassCode,
} from './referral';

// Module-level, so StrictMode's double effect and a remount of the app shell
// can never POST twice: one redemption at a time, and each code is tried at
// most once per launch. A code that met a network failure stays stashed and is
// tried again on a later launch.
let running = false;
const triedThisLaunch = new Set();

/**
 * Redeems a pass code stashed before sign-in — from the babytrack://pass/<code>
 * link or the /pass/:code page — once the user is signed in. Mounted once in
 * the authenticated shell (like usePendingInvite), and it also picks up a code
 * stashed while the app is open (a pass link tapped by a signed-in user).
 *
 * Background work: silent on transient failures, but a definitive outcome is
 * worth one toast — the person tapped a pass link and expects to hear.
 */
export function usePendingPass() {
  const queryClient = useQueryClient();
  const code = useSyncExternalStore(subscribeStashedPassCode, readStashedPassCode, () => null);

  useEffect(() => {
    if (!code || running || triedThisLaunch.has(code)) return;
    if (!getCachedIdToken()) return; // signed out — wait for the sign-in
    running = true;
    triedThisLaunch.add(code);

    redeemPassCode(code)
      .then((result) => {
        if (result.ok) {
          queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
          queryClient.invalidateQueries({ queryKey: ['entitlement'] });
          const endsOn = formatPassEnd(result.pass?.endsAt);
          toast.success(
            endsOn
              ? `Your ${PASS_DAYS}-day Premium pass is active until ${endsOn}`
              : `Your ${PASS_DAYS}-day Premium pass is active`
          );
        } else if (isDefinitivePassResult(result)) {
          // redeemPassCode has already cleared the stash for a final answer.
          toast(passReasonMessage(result.reason), { duration: 6000 });
        }
        // 'network' / 'error': the code stays stashed for a later launch.
      })
      .finally(() => {
        running = false;
      });
  }, [code, queryClient]);
}

export default usePendingPass;
